import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Plus } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import DataTable from '../components/DataTable';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { getDrivers } from '../api/driversApi';
import { useToast } from '../context/ToastContext';

function Drivers() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchDrivers = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const result = await getDrivers(pageNum, 10);
      if (result.success) {
        setDrivers((prev) => (append ? [...prev, ...result.drivers] : result.drivers));
		console.log('Drivers data:', result.drivers);
        setHasMore(result.has_more);
        setPage(pageNum);
      } else {
        setError(result.message || t('common.genericError'));
        showToast('error', result.message || t('common.genericError'));
      }
    } catch (err) {
      setError(t('common.networkError'));
      showToast('error', t('common.networkError'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchDrivers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKey: 'driver_name',
      header: 'Name',
      cell: (info) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate(`/drivers/${info.row.original.id_driver}`)}
        >
          {info.getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: (info) => (
        <span
          style={{
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            backgroundColor: info.getValue() ? '#d1fae5' : '#fee2e2',
            color: info.getValue() ? '#065f46' : '#991b1b',
          }}
        >
          {info.getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    { accessorKey: 'email', header: 'Email' },
    { accessorKey: 'phone', header: 'Phone' },
    {
      accessorKey: 'default_rate',
      header: 'Default Rate',
      cell: (info) => (info.getValue() != null ? `${info.getValue()}%` : '—'),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: (info) => info.getValue()?.split(' ')[0] || '—',
    },
  ];

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={t('nav.drivers')}
          rightAction={
            <button onClick={() => navigate('/drivers/create')} style={styles.createBtn}>
              <Plus size={16} style={{ marginRight: 6 }} />
              Create Driver
            </button>
          }
        />

        <div style={{ padding: 24 }}>
          {loading && <LoadingSpinner fullScreen />}

          {!loading && error && (
            <EmptyState
              icon={Users}
              title={t('common.genericError')}
              message={error}
              onRetry={() => fetchDrivers(1)}
              retryLabel={t('common.tryAgain')}
            />
          )}

          {!loading && !error && drivers.length === 0 && (
            <EmptyState
              icon={Users}
              title="No drivers yet"
              message="Create your first driver to get started."
            />
          )}

          {!loading && !error && drivers.length > 0 && (
            <>
              <DataTable columns={columns} data={drivers} />

              {hasMore && (
                <button
                  onClick={() => fetchDrivers(page + 1, true)}
                  disabled={loadingMore}
                  style={styles.loadMoreBtn}
                >
                  {loadingMore ? t('common.loading') : t('common.loadMore')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  createBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  loadMoreBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    color: '#2563eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 12,
  },
};

export default Drivers;